import { hasValue } from "@arti/utilities";
import { motion } from "framer-motion";
/**
 * Scrollable event stream pane component with auto-scroll behavior.
 */
import { type FC, useEffect, useMemo, useRef, useState } from "react";

import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";
import type {
  AccentColor,
  EventStreamConfig,
  PhaseStatusData,
  StreamModelInfo,
} from "./types";

import { ActivePulseIndicator } from "../../common/ActivePulseIndicator";
import { Text } from "../../common/Text";
import { Tooltip } from "../../common/Tooltip";
import { SEVERITY_COLORS } from "./eventColors";
import {
  enrichProviderProgressEvents,
  filterVisibleProviderProgressEvents,
} from "./eventDisplay";
import { ModelBadge } from "./ModelBadge";
import { PromptInspectorModal } from "./PromptInspectorModal";
import { ProviderEventRow } from "./ProviderEventRow";
import { ProviderProgressActionMenu } from "./ProviderProgressActionMenu";
import { ProviderProgressTranscriptModal } from "./ProviderProgressTranscriptModal";
import { type RunLifecycleStatus, getRunLifecycleStatus } from "./runLifecycle";

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === "string");
}

function readPhaseValue(event: ProviderProgressEntry): string | null {
  const { phase } = event.event_data;
  return typeof phase === "string" && phase.length > 0 ? phase : null;
}

interface EventStreamPaneProps {
  streamConfig: EventStreamConfig;
  events: ProviderProgressEntry[];
  phaseStatus?: PhaseStatusData | null;
  modelInfo?: StreamModelInfo | null;
  maxHeightClass?: string;
  visibleEventTypes?: ReadonlySet<string>;
  isEventTypeFilterActive?: boolean;
}

/** Formats token count for display (e.g., 1500 -> "1.5k") */
function formatTokens(tokens: number): string {
  if (tokens < 1000) {
    return tokens.toString();
  }
  if (tokens < 1_000_000) {
    return `${(tokens / 1000).toFixed(1)}k`;
  }
  return `${(tokens / 1_000_000).toFixed(2)}M`;
}

function formatStepCount(steps: number): string {
  const roundedSteps = Math.max(1, Math.round(steps));
  return `${roundedSteps} step${roundedSteps === 1 ? "" : "s"}`;
}

/* ------------------------------------------------------------------ */
/*  Run lifecycle header indicator                                      */
/* ------------------------------------------------------------------ */

const RUN_STATUS_INDICATOR: Record<RunLifecycleStatus, FC> = {
  active: () => <ActivePulseIndicator size="sm" />,
  ended: () => (
    <div className="flex h-2.5 w-2.5 items-center justify-center">
      <svg
        className="h-2.5 w-2.5 text-emerald-500 dark:text-emerald-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  ),
  error: () => (
    <div className="h-2 w-2 bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.5)] dark:bg-red-400 dark:shadow-[0_0_6px_rgba(248,113,113,0.4)]" />
  ),
  idle: () => <div className="h-2 w-2 bg-slate-400 dark:bg-slate-500" />,
};

/* ------------------------------------------------------------------ */
/*  Budget indicator                                                    */
/* ------------------------------------------------------------------ */

const StartupBudgetOffsetIndicator: FC<{ deductedInputTokens: number }> = ({
  deductedInputTokens,
}) => {
  if (deductedInputTokens <= 0) {
    return null;
  }

  return (
    <Tooltip
      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-none border border-slate-300 font-mono text-[9px] font-bold text-slate-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 focus-visible:border-emerald-400 focus-visible:bg-emerald-50 focus-visible:text-emerald-700 dark:border-slate-700 dark:text-slate-300 dark:hover:border-emerald-500/40 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-200 dark:focus-visible:border-emerald-500/40 dark:focus-visible:bg-emerald-500/10 dark:focus-visible:text-emerald-100"
      content={`${formatTokens(deductedInputTokens)} startup tokens were deducted from the initial phase baseline.`}
      tooltipClassName="text-center"
      triggerTabIndex={0}
    >
      <span>i</span>
    </Tooltip>
  );
};

const PhaseBudgetIndicator: FC<{
  budget: NonNullable<PhaseStatusData["budget"]>;
  barClassName: string;
  textClassName: string;
}> = ({ budget, barClassName, textClassName }) => {
  const burnRate = budget.averageBurnRateInputTokensPerStep;
  const burnRateLabel =
    burnRate !== null ? `${formatTokens(Math.round(burnRate))}/step` : null;
  const burnRateTooltip =
    burnRate !== null
      ? `Average phase budget burn so far: ${formatTokens(Math.round(burnRate))} tokens per step.${budget.estimatedRemainingSteps !== null ? ` At this pace, about ${formatStepCount(budget.estimatedRemainingSteps)} remain before the phase budget is exhausted.` : ""}`
      : null;

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-none bg-slate-200 dark:bg-slate-700">
        <div
          className={`h-full transition-all duration-300 ${barClassName}`}
          style={{ width: `${Math.min(budget.percentUsed, 100)}%` }}
        />
      </div>
      <span className={`font-mono text-[10px] font-medium ${textClassName}`}>
        {formatTokens(budget.usedInputTokens)}/
        {formatTokens(budget.maxInputTokens)}
      </span>
      {burnRateLabel && burnRateTooltip ? (
        <Tooltip
          className="inline-flex items-center gap-1 rounded-sm border border-slate-300/80 bg-slate-100/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 transition-colors hover:border-slate-400 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:bg-slate-800"
          content={burnRateTooltip}
          tooltipClassName="max-w-xs text-center"
          triggerTabIndex={0}
        >
          <span className={`h-1.5 w-1.5 ${barClassName}`} />
          <span>burn</span>
          <span className={`normal-case tracking-normal ${textClassName}`}>
            {burnRateLabel}
          </span>
        </Tooltip>
      ) : null}
      <StartupBudgetOffsetIndicator
        deductedInputTokens={budget.deductedInputTokens}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Phase timeline                                                      */
/* ------------------------------------------------------------------ */

const PhaseTimelineMarker: FC<{
  isCurrent: boolean;
  isVisited: boolean;
  onClick: () => void;
  phase: string;
  streamId: string;
  tabId: string;
}> = ({ isCurrent, isVisited, onClick, phase, streamId, tabId }) => {
  let phaseBlockClass =
    "border border-slate-700 bg-transparent opacity-40 cursor-not-allowed";
  if (isCurrent) {
    phaseBlockClass = "bg-emerald-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]";
  } else if (isVisited) {
    phaseBlockClass =
      "border border-slate-500 bg-slate-700 hover:border-emerald-500 hover:bg-slate-600 cursor-pointer";
  }

  return (
    <Tooltip content={`Phase: ${phase}`}>
      <motion.button
        layoutId={isVisited ? `inspector-${streamId}-${tabId}` : undefined}
        onClick={onClick}
        disabled={!isVisited}
        className={`relative h-3 w-3 rounded-none transition-all duration-200 ${phaseBlockClass}`}
        aria-label={`Phase ${phase}`}
      >
        {isCurrent ? (
          <span className="absolute -inset-1 animate-ping rounded-none border border-emerald-500/50" />
        ) : null}
      </motion.button>
    </Tooltip>
  );
};

/* ------------------------------------------------------------------ */
/*  Main pane                                                           */
/* ------------------------------------------------------------------ */

export const EventStreamPane: FC<EventStreamPaneProps> = ({
  streamConfig,
  events,
  phaseStatus,
  modelInfo,
  maxHeightClass = "max-h-72",
  visibleEventTypes,
  isEventTypeFilterActive = false,
}) => {
  const { accentColor, id: streamId, label: title } = streamConfig;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const prevEventCountRef = useRef(events.length);
  const hasInitialScrolledRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);

  const isNearBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) {
      return true;
    }
    const threshold = 50;
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold
    );
  };

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }
    shouldAutoScrollRef.current = isNearBottom();
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    if (!hasInitialScrolledRef.current && events.length > 0) {
      isProgrammaticScrollRef.current = true;
      container.scrollTop = container.scrollHeight;
      hasInitialScrolledRef.current = true;
      prevEventCountRef.current = events.length;
      return;
    }

    if (
      events.length > prevEventCountRef.current &&
      shouldAutoScrollRef.current
    ) {
      isProgrammaticScrollRef.current = true;
      container.scrollTop = container.scrollHeight;
    }
    prevEventCountRef.current = events.length;
  }, [events.length]);

  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

  const enrichedEvents = useMemo(
    () => enrichProviderProgressEvents(events),
    [events],
  );
  const visibleEvents = useMemo(
    () =>
      filterVisibleProviderProgressEvents(enrichedEvents, visibleEventTypes),
    [enrichedEvents, visibleEventTypes],
  );

  // Accent color maps
  const accentBorderMap: Record<AccentColor, string> = {
    amber: "border-amber-500/50",
    cyan: "border-cyan-500/50",
    emerald: "border-emerald-500/50",
    gray: "border-slate-300 dark:border-slate-700",
    indigo: "border-emerald-500/50",
    rose: "border-rose-500/50",
    violet: "border-violet-500/50",
  };

  const accentTitleMap: Record<AccentColor, string> = {
    amber: "text-amber-500 dark:text-amber-400",
    cyan: "text-cyan-500 dark:text-cyan-400",
    emerald: "text-emerald-500 dark:text-emerald-400",
    gray: "text-slate-500 dark:text-slate-300",
    indigo: "text-emerald-500 dark:text-emerald-400",
    rose: "text-rose-500 dark:text-rose-400",
    violet: "text-violet-500 dark:text-violet-400",
  };

  const effectiveAccentColor: AccentColor = accentColor ?? "gray";
  const accentClasses = accentBorderMap[effectiveAccentColor];
  const titleColorClasses = accentTitleMap[effectiveAccentColor];

  const severityColors = phaseStatus?.budget
    ? SEVERITY_COLORS[phaseStatus.budget.severity]
    : null;

  const [isInspectorOpen, setIsInspectorOpen] = useState(false);
  const [initialInspectorTab, setInitialInspectorTab] = useState<
    string | undefined
  >();

  const openInspector = (tabId?: string) => {
    setInitialInspectorTab(tabId);
    setIsInspectorOpen(true);
  };

  const { phases, visitedPhases } = useMemo(() => {
    const runStart = events.find((e) => e.event_type === "run:start");
    const modePhases = readStringArray(runStart?.event_data.phases);

    const visited = new Set<string>();
    for (const e of events) {
      if (
        e.event_type === "phase:system-prompt" ||
        e.event_type === "phase:status"
      ) {
        const p = readPhaseValue(e);
        if (p) {
          visited.add(p);
        }
      }
    }

    return { phases: modePhases, visitedPhases: visited };
  }, [events]);

  const runStatus = useMemo(() => getRunLifecycleStatus(events), [events]);
  const runIsActive = runStatus === "active";
  const StatusIndicator = RUN_STATUS_INDICATOR[runStatus];

  return (
    <div
      className={`relative flex flex-col rounded-none border overflow-hidden bg-white shadow-sm dark:bg-arti-background ${accentClasses}`}
    >
      {/* Decorative terminal grid background */}
      <div className="terminal-grid-bg absolute inset-0 z-0 pointer-events-none" />

      <div className="relative z-10 flex flex-col flex-1">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5 dark:border-slate-700">
          <div className="flex min-w-0 items-center gap-2">
            <StatusIndicator />
            <span
              className={`min-w-0 truncate font-mono text-[11px] font-bold uppercase tracking-[0.2em] ${titleColorClasses}`}
            >
              {title}
            </span>
          </div>
          <span className="border border-slate-200 bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
            {visibleEvents.length} EVENTS
          </span>
        </div>

        {/* Status bar: phase + budget on separate rows for clarity */}
        {(hasValue(phaseStatus) || hasValue(modelInfo)) && (
          <div className="space-y-2 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/80">
            {/* Phase row */}
            {phaseStatus && (
              <div className="flex flex-wrap items-center gap-2">
                <ProviderProgressActionMenu
                  transcriptDisabled={enrichedEvents.length === 0}
                  onOpenInspector={() => openInspector()}
                  onOpenTranscript={() => setIsTranscriptOpen(true)}
                />
                <span
                  className={`border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${
                    runIsActive
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500 dark:text-emerald-400"
                      : "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  {phaseStatus.phase.replace(/_/g, " ")}
                </span>
                {/* Phase Timeline */}
                {phases.length > 0 && (
                  <div className="flex items-center gap-1.5 border-l-2 border-slate-300 pl-2 dark:border-slate-600">
                    {phases.map((p) => {
                      const isCurrent = runIsActive && p === phaseStatus.phase;
                      const isVisited = visitedPhases.has(p);
                      const tabId = `phase-${p}`;

                      return (
                        <PhaseTimelineMarker
                          key={p}
                          isCurrent={isCurrent}
                          isVisited={isVisited}
                          onClick={() => {
                            if (isVisited) {
                              openInspector(tabId);
                            }
                          }}
                          phase={p}
                          streamId={streamId}
                          tabId={tabId}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {/* Budget + context row */}
            {phaseStatus?.budget && severityColors && (
              <div className="flex flex-wrap items-center gap-3">
                {phaseStatus.contextWindowSize !== undefined && (
                  <div className="flex items-center gap-1">
                    <Text>ctx:</Text>
                    <span className="font-mono text-[10px] font-medium text-slate-700 dark:text-slate-300">
                      {formatTokens(phaseStatus.contextWindowSize)}
                    </span>
                  </div>
                )}
                <PhaseBudgetIndicator
                  barClassName={severityColors.bar}
                  budget={phaseStatus.budget}
                  textClassName={severityColors.text}
                />
              </div>
            )}
            {/* Model row */}
            {modelInfo && (
              <ModelBadge
                modelRef={modelInfo.currentModel}
                reasoningEffort={modelInfo.reasoningEffort}
              />
            )}
          </div>
        )}

        {/* Event list with bottom fade */}
        <div className="relative flex-1">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={`custom-scrollbar divide-y divide-slate-100 overflow-y-auto overflow-x-hidden dark:divide-slate-800 ${maxHeightClass}`}
          >
            {visibleEvents.map((event, index) => (
              <ProviderEventRow
                key={`${event.received_at}-${index}`}
                event={event}
              />
            ))}
            {visibleEvents.length === 0 && (
              <div className="py-6 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                {isEventTypeFilterActive
                  ? "No events match the current filter."
                  : "No events yet"}
              </div>
            )}
          </div>
          {/* Bottom fade gradient */}
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white dark:from-arti-background" />
        </div>

        {isInspectorOpen && (
          <PromptInspectorModal
            events={events}
            streamLabel={title || "Agent"}
            initialTabId={initialInspectorTab}
            layoutId={
              initialInspectorTab
                ? `inspector-${streamId}-${initialInspectorTab}`
                : undefined
            }
            onClose={() => setIsInspectorOpen(false)}
          />
        )}
        {isTranscriptOpen && (
          <ProviderProgressTranscriptModal
            events={events}
            streamLabel={title || "Agent"}
            visibleEventTypes={visibleEventTypes}
            isEventTypeFilterActive={isEventTypeFilterActive}
            onClose={() => setIsTranscriptOpen(false)}
          />
        )}
      </div>
    </div>
  );
};
